import type { WireFlexEntity, WireFlexRow } from '@/api/wire';
import { others, agoWords, dayOf, daysSince, nowOf, plural, type Viewer } from '@/directory/explainKit';
import type { ItemContext, Party } from '@/ui/context';
import { money, when } from '@/ui/records';
import type { Who } from '@/ui/who';
import { humanise, stateWords } from '@/ui/words';

/**
 * What a department-defined record says about itself (ADR-0155). A flexible
 * record is whatever a live feature of the department keeps (§5): the
 * columns come from the table, so nothing here knows a Deal in advance, and
 * a line is said only when the row holds its field. A row carries no author
 * and no owner unless its kind has such a column; where it has none the
 * panel leaves the line out rather than say nobody owns it.
 */

export const HIDDEN = new Set(['id', 'created_at', 'meta']);

/** `company_id` → "Company", `closed_at` → "Closed". */
export function fieldLabel(field: string): string {
  return field.replace(/_id$/, '').replace(/_at$/, '').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}

/** A stored value as a person reads it; null when the row holds nothing. */
export function renderField(field: string, value: unknown, names: ReadonlyMap<string, string>): string | null {
  if (value === null || value === undefined) return null;
  if (field.endsWith('_at')) return when(String(value));
  if (field.endsWith('_id')) return names.get(String(value)) ?? 'A linked record';
  if (Array.isArray(value)) return value.map(String).join(', ');
  if (typeof value === 'object') return Object.entries(value as Record<string, unknown>).map(([k, v]) => `${humanise(k)}: ${String(v)}`).join('; ');
  return String(value);
}

/** What a kind is, as the page's own blurb says it (Flex.tsx BLURB). */
const KIND: Record<string, string> = {
  Deal: 'a deal in the pipeline',
  Order: 'an order, created when a deal is won',
  OrderItem: 'a line of an order',
  Sample: 'a sample that went out',
  Invoice: 'an invoice',
};

const CLOSED = new Set(['won', 'lost', 'paid', 'delivered', 'closed', 'cancelled', 'done', 'completed', 'declined', 'feedback_received']);
/** A deal nobody has touched for this long is worth asking about. */
export const STALE_DAYS = 30;

const OWNER_KEYS = ['owner', 'owner_email', 'assigned_to', 'assignee'];
const MAKER_KEYS = ['created_by', 'created_by_email', 'author'];

const text = (row: WireFlexRow, key: string): string | null => {
  const v = row[key];
  return typeof v === 'string' && v.trim() ? v : null;
};

export function recordTitle(kind: WireFlexEntity, row: WireFlexRow): string {
  return (text(row, 'title') ?? text(row, 'number') ?? text(row, 'product') ?? `A ${humanise(kind.entity).toLowerCase()} recorded ${when(row.created_at)}`) as string;
}

export interface FlexExtra {
  /** Company names by id, as the screen already reads them. */
  names: ReadonlyMap<string, string>;
  /** On its own page: no link back to itself, and its fields are shown right below. */
  onPage?: boolean | undefined;
}

export function explainFlexRow(kind: WireFlexEntity, row: WireFlexRow, who: Who, v: Viewer, extra: FlexExtra): ItemContext {
  const now = nowOf(v);
  const title = recordTitle(kind, row);
  const noun = humanise(kind.entity).toLowerCase();
  const status = text(row, 'status');
  const closed = status !== null && CLOSED.has(status);
  const touched = text(row, 'updated_at') ?? row.created_at;
  const idle = daysSince(touched, now);
  const stale = !closed && idle !== null && idle >= STALE_DAYS;
  const isDeal = kind.entity === 'Deal';
  const where = extra.onPage ? 'with the buttons above' : 'from its page';
  const fields = kind.fields.filter((f) => !HIDDEN.has(f.name) && f.name !== 'currency' && f.name !== 'status' && f.name !== 'updated_at');

  const party = (keys: string[], role: string): Party | null | undefined => {
    const key = keys.find((k) => kind.fields.some((f) => f.name === k));
    if (!key) return undefined;
    const v_ = text(row, key);
    if (!v_) return null;
    const p = who.actor(v_);
    return p ? { ...p, role } : null;
  };
  const owner = party(OWNER_KEYS, 'owns it');
  const maker = party(MAKER_KEYS, 'recorded it');

  const company = text(row, 'company_id') ? extra.names.get(text(row, 'company_id')!) : undefined;
  const dealId = text(row, 'deal_id');
  const orderId = text(row, 'order_id');
  const source: ItemContext['source'] = dealId
    ? { label: `Created from ${kind.entity === 'Order' ? 'the deal that was won' : 'its deal'}`, to: `/records/Deal/${dealId}` }
    : orderId
      ? { label: kind.entity === 'OrderItem' ? 'A line of its order' : 'Raised for its order', to: `/records/Order/${orderId}` }
      : null;

  const next = ((): ItemContext['next'] => {
    if (closed) {
      return {
        tip: isDeal ? `Nothing to do. This deal is ${status}; it stays on record. If the customer comes back, record a new deal.` : `Nothing to do. It is ${stateWords(status!).toLowerCase()}.`,
        instead: isDeal && status === 'lost' ? 'The reason it was lost is on its page, and Insight counts it with the others.' : 'Open it only to look something up.',
      };
    }
    if (isDeal && stale) {
      return {
        tip: `Not changed in ${plural(idle!, 'day')}. Ask whoever holds the account where it stands, then mark it Won or Lost ${where}, or Edit it if something moved.`,
        instead: 'Or leave it open: nothing closes by itself, but it keeps showing as stale in the pipeline.',
      };
    }
    if (isDeal) {
      return {
        tip: `Open and recently touched. Mark it Won ${where} when the customer agrees (the department’s order capture runs then), or Lost with the reason.`,
        instead: 'Nothing is needed until the customer decides; Edit it if the value or the contact changes.',
      };
    }
    if (status === 'overdue') {
      return {
        tip: `It is overdue. Write to them about it ${extra.onPage ? '' : 'after opening it '}— your twin can draft a reminder, and you read it and send it.`,
        instead: 'Or check first whether it was paid and not recorded, and Edit the status.',
      };
    }
    if (stale) {
      return {
        tip: `Not changed in ${plural(idle!, 'day')}. Open it and check it still stands; Edit it if something moved.`,
        instead: 'Or leave it: nothing here closes by itself.',
      };
    }
    return {
      tip: `Nothing is needed from you now. Open it to see where it stands, and Edit it when something changes.`,
      instead: `It was recorded ${agoWords(row.created_at, now) ?? 'recently'}${source ? ' from something already on record' : ''}.`,
    };
  })();

  // An address a column holds is a person: shown by name, as the reader is "You".
  const show = (name: string, value: unknown): string | null =>
    typeof value === 'string' && value.includes('@') ? (who.actor(value)?.name ?? null) : renderField(name, value, extra.names);
  const shown = extra.onPage
    ? []
    : fields
        .map((f): [string, string | null] => [fieldLabel(f.name), f.type.startsWith('numeric') && 'currency' in row ? money(row[f.name], row['currency']) : show(f.name, row[f.name])])
        .filter(([, val]) => val !== null)
        .slice(0, 5);

  return {
    subject: { kind: noun, title },
    summary: `${title} is ${KIND[kind.entity] ?? `a ${noun} record`}${company ? ` for ${company}` : ''}${status ? `, ${stateWords(status).toLowerCase()}` : ''}. It is kept by ${who.label(v.department)}’s own ${noun} feature.`,
    need: closed ? 'Nothing: it is finished.' : isDeal ? 'A decision when the customer makes one: won or lost, with the reason.' : stale ? 'A check that it still stands.' : 'Nothing right now.',
    why: `${who.label(v.department)} keeps its ${noun}s as its own records, so it is in front of you in that department.`,
    source,
    // Only a kind with an author column can say who recorded it.
    ...(maker === undefined ? {} : { broughtBy: maker }),
    ...(owner === undefined ? {} : { owner }),
    knows: others([owner, maker, ...who.knowers(v.department)]),
    facts: [
      ...(status ? ([['Status', stateWords(status)]] as Array<[string, string | null]>) : []),
      ...shown,
      ['Recorded', dayOf(row.created_at)],
      ['Last changed', text(row, 'updated_at') ? agoWords(touched, now) : null],
    ],
    next,
    ...(extra.onPage ? {} : { open: { to: `/records/${kind.entity}/${row.id}` } }),
    ...(isDeal && stale ? { task: { title: `Check where “${title}” stands`, from: `/records/Deal/${row.id}` } } : {}),
  };
}
